import React, { useState, useCallback, useEffect } from 'react'
import CustomDialog from 'sharedComponents/customDialog'
import useStyles from './style'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { Grid, TextField, Autocomplete, Typography } from '@mui/material'
import DropZone from './dropZone'
import _ from 'lodash'
import imageUploadApi from 'services/imageUpload'
import contactUsApi from 'services/contactUs'
import { showLoader, hideLoader } from 'redux/loader/actions'
import { showSnackbar } from 'redux/snackbar/actions'
import { useDispatch } from 'react-redux'
import { API_ENDPOINTS } from 'constants'
import DeleteIcon from '@mui/icons-material/Delete'

const typeOptions = [
  { label: 'Gold', value: 'gold' },
  { label: 'Diamond', value: 'diamond' },
  { label: 'Platinum', value: 'platinum' },
  { label: 'Silver', value: 'silver' },
]

const CustomRequestModal = (props) => {
  const { open, handleClose, title, btnLabel } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const [metalTypeOptions, setMetalTypeOptions] = useState([])
  const [uploadErros, setUploadErros] = useState([])
  const [images, setImages] = useState([])

  // Send custom request
  const handleContactUs = (formValues) => {
    const data = {
      name: formValues?.name,
      mobilenum: formValues?.mobileNo,
      email: formValues?.email,
      category_id: formValues?.type?._id,
      image: images?.map((data) => data?.filename),
      message: formValues?.message,
    }
    dispatch(showLoader('Loading please wait...'))
    const onSuccess = (res) => {
      dispatch(hideLoader())
      if (res?.data?.status?.toLowerCase() === 'true') {
        dispatch(
          showSnackbar({
            message: res?.data?.message || 'Request sent successfully',
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            variant: 'success',
          })
        )
        formik.resetForm()
        handleClose()
      }
    }
    const onFailure = (err) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || 'Failed to fetch data',
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          variant: 'error',
        })
      )
      console.log('save custom request Api', err)
    }
    contactUsApi.sendCustomRequest({ ...data }).then(onSuccess, onFailure)
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      mobileNo: "",
      email: "",
      type: "",
      image: "",
      message: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().trim().required("Name is required"),
      mobileNo: yup
        .string()
        .required("Mobile number is required")
        .matches(/^[0-9]+$/, "Only Numbers are allowed")
        .max(10, `Max 10 digits are allowed`)
        .min(10, `Must be exactly 10 numbers`)
        .nullable(),
      email: yup
        .string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email address"
        )
        .email("Enter a valid email address"),
      type: yup
        .object()
        .required("Jewellery metal type is required")
        .nullable(),
      image: yup.string().required("Please upload a image"),
      message: yup.string().max(500, "Maximum 500 characters are allowed"),
    }),
    onSubmit: handleContactUs,
  });

  // get metal type api
  const getMetalTypes = useCallback(() => {
    dispatch(showLoader('Loading please wait...'))
    const onSuccess = (res) => {
      dispatch(hideLoader())
      if (res?.data?.status?.toLowerCase() === 'true') {
        res?.data?.data?.results?.[0] &&
          setMetalTypeOptions(res?.data?.data?.results)
      } else {
        setMetalTypeOptions([])
      }
    }
    const onFailure = (err) => {
      dispatch(hideLoader())
      console.log('Get metal types options Api', err)
    }
    contactUsApi.getMetalTypes().then(onSuccess, onFailure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    open && getMetalTypes()
  }, [getMetalTypes, open])

  // Upload image api
  const uploadImages = (acceptedFiles) => {
    dispatch(showLoader('Loading please wait...'))
    const onSuccess = (res) => {
      dispatch(hideLoader())
      if (res?.data?.status?.toLowerCase() === 'true') {
        formik?.setFieldValue('image', res?.data?.data?.[0]?.filename)
        setImages(res?.data?.data)
      } else {
        setImages([])
      }
    }
    const onFailure = (err) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || 'Failed to fetch data',
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          variant: 'error',
        })
      )
      console.log('Get delivery process details Api', err)
    }

    const formData = new FormData()
    acceptedFiles?.map((file) => formData.append('files', file))
    imageUploadApi.saveMultiImage(formData).then(onSuccess, onFailure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  // Handle remove image locally
  const handleDeleteImage = (filename) => {
    const updatedImages = images?.filter((data) => data?.filename !== filename)
    setImages(updatedImages)
  }

  return (
    <>
      <CustomDialog
        open={open}
        handleClose={() => {
          handleClose()
          setUploadErros([])
          formik.resetForm()
          setImages([])
        }}
        title={title}
        btnLabel={btnLabel}
        onSubmit={formik?.handleSubmit}
      >
        <Grid container flexDirection={'column'} className={classes.root}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              id="name"
              value={formik?.values?.name}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Name*"
              size="small"
              error={formik?.errors?.name && formik?.touched?.name}
              helperText={
                formik?.errors?.name &&
                formik?.touched?.name &&
                formik?.errors?.name
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="mobileNo"
              id="mobileNo"
              value={formik?.values?.mobileNo}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Mobile No*"
              size="small"
              error={formik?.errors?.mobileNo && formik?.touched?.mobileNo}
              helperText={
                formik?.errors?.mobileNo &&
                formik?.touched?.mobileNo &&
                formik?.errors?.mobileNo
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              id="email"
              value={formik?.values?.email}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Email"
              size="small"
              error={formik?.errors?.email && formik?.touched?.email}
              helperText={
                formik?.errors?.email &&
                formik?.touched?.email &&
                formik?.errors?.email
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disablePortal
              disableClearable
              size="small"
              fullWidth
              name="type"
              id="type"
              options={metalTypeOptions}
              value={formik?.values?.type}
              getOptionLabel={(option) => option.name || ''}
              onChange={(e, value) => {
                formik?.setFieldValue('type', value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Metal type*"
                  error={formik?.errors?.type && formik?.touched?.type}
                  helperText={
                    formik?.errors?.type &&
                    formik?.touched?.type &&
                    formik?.errors?.type
                  }
                />
              )}
            />
          </Grid>
          {images?.length === 3 ? (
            <></>
          ) : (
            <Grid item xs={12}>
              <section className={classes.uploadContainer}>
                <DropZone
                  upload={uploadImages}
                  setUploadErros={setUploadErros}
                />
              </section>
            </Grid>
          )}
          {formik?.errors?.image && formik?.touched?.image && (
            <Grid item xs={12} className={classes.error}>
              <Typography variant="subtitle1">
                {formik?.errors?.image &&
                  formik?.touched?.image &&
                  formik?.errors?.image}
              </Typography>
            </Grid>
          )}
          {!_.isEmpty(uploadErros) && (
            <Grid item xs={12} style={{ paddingBlock: 8 }}>
              {uploadErros.map(({ file, errors }) => (
                <div key={file.path}>
                  {errors?.map((e) => (
                    <Typography
                      style={{ color: 'red' }}
                      variant="subtitle1"
                      key={e.code}
                    >
                      {e.message}
                    </Typography>
                  ))}
                </div>
              ))}
            </Grid>
          )}
          {!_.isEmpty(images) ? (
            <Grid item xs={12}>
              <section className={classes.imgWrapper}>
                {images?.map((data) => (
                  <div className={classes.imgContainer}>
                    <img
                      src={API_ENDPOINTS.FILE_URL + data?.filename}
                      alt={data?.originalname}
                      className={classes.img}
                    />
                    <div className={classes.deleteIcon}>
                      <DeleteIcon
                        onClick={() => handleDeleteImage(data?.filename)}
                      />
                    </div>
                  </div>
                ))}
              </section>
            </Grid>
          ) : (
            <></>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              name="message"
              id="message"
              value={formik?.values?.message}
              onChange={formik?.handleChange}
              type={'text'}
              fullWidth
              variant="outlined"
              label="Message"
              size="small"
              multiline
              rows={5}
              rowsMax={5}
              error={formik?.errors?.message && formik?.touched?.message}
              helperText={
                formik?.errors?.message &&
                formik?.touched?.message &&
                formik?.errors?.message
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.caption}>
            <Typography variant="subtitle1">
              *One of our representatives will contact you shortly to discuss
              your requirements and provide you with more information on our
              custom jewellery design process.
            </Typography>
          </Grid>
        </Grid>
      </CustomDialog>
    </>
  )
}

export default CustomRequestModal
